import React, { useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ahoraDeLaApp, ratoCorto } from '../lib/horaEspana';

/**
 * LA FECHA DE UNA CUENTA DE PRUEBA (prueba 0.2 del encargo de Jesús, 02-11): «mover la fecha
 * de una cuenta de prueba para recorrer un ciclo de doce semanas en una tarde y ver cada
 * aviso saltar en su día».
 *
 * Solo sale en una cuenta a la que un admin le activó la fecha (`minutos_por_delante`, también
 * 0). Mueve la fecha y la hora hacia delante y recarga la app, para que cada pantalla se pinte
 * con su momento nuevo. Hacia atrás no se puede: lo explica `core/fecha_de_prueba`.
 */
const HORA = 60;
const DIA = 24 * HORA;

const momentoCorto = (fecha) => `${fecha.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })} · ${fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;

/** El valor de un `datetime-local` («2026-11-09T15:30»). */
const comoCampo = (fecha) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}T${p(fecha.getHours())}:${p(fecha.getMinutes())}`;
};

export default function FechaDePrueba() {
    const { user, api } = useAuth();
    const [abierto, setAbierto] = useState(false);
    const [moviendo, setMoviendo] = useState(false);
    const [destino, setDestino] = useState('');
    const actuales = user?.minutos_por_delante;
    if (actuales === undefined || actuales === null) return null;

    const ir = async (minutos) => {
        if (moviendo || minutos <= actuales) return;
        setMoviendo(true);
        try {
            await api.put('/settings/fecha-de-prueba', { minutos });
            window.location.reload();
        } catch (e) {
            console.error('fecha de prueba: no se pudo mover', e);
            toast.error('No se pudo mover la fecha. Vuelve a intentarlo.');
            setMoviendo(false);
        }
    };

    // SOLO AL PULSAR «IR» (revisor, 02-11, F7): el campo cambia a cada número que se teclea, y
    // saltar con el primero que forma una fecha válida dejaba la cuenta en otro momento sin
    // vuelta atrás. Los minutos se cuentan desde el reloj de verdad, que es lo que guarda el
    // servidor.
    const objetivo = destino ? Math.ceil((Date.parse(destino) - Date.now()) / 60000) : 0;
    const irAlDestino = () => { if (objetivo > actuales) ir(objetivo); };

    if (!abierto) {
        return (
            <button type="button" onClick={() => setAbierto(true)} data-testid="fecha-de-prueba"
                className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full border border-sky-700 bg-sky-600 px-3 py-2 shadow-lg text-xs font-semibold text-white whitespace-nowrap">
                <CalendarClock className="w-4 h-4" />
                <span>{momentoCorto(ahoraDeLaApp())}</span>
                {/* En el móvil no cabe sin tapar el logo: lo dice el panel al abrirlo. */}
                {actuales > 0 && <span className="hidden sm:inline text-sky-100">+{ratoCorto(actuales)}</span>}
            </button>
        );
    }

    const boton = 'rounded-lg bg-sky-500 text-black text-sm font-bold py-2 hover:bg-sky-400 disabled:opacity-50';
    return (
        <div data-testid="fecha-de-prueba-abierta"
            className="fixed top-2 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-80 z-[60] rounded-2xl border border-sky-500/50 bg-ink/95 backdrop-blur p-4 shadow-xl text-sky-50">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-[11px] uppercase tracking-wider text-sky-300/80 font-semibold">Fecha de prueba</p>
                    <p className="text-sm font-bold mt-0.5">{momentoCorto(ahoraDeLaApp())}</p>
                    <p className="text-xs text-sky-100/70 mt-0.5">
                        {actuales > 0 ? `Esta cuenta va ${ratoCorto(actuales)} por delante.` : 'Esta cuenta va con la hora de verdad.'}
                    </p>
                </div>
                <button type="button" onClick={() => setAbierto(false)} aria-label="Cerrar"
                    className="w-8 h-8 -mr-1 -mt-1 rounded-lg flex items-center justify-center text-sky-100/60 hover:text-white hover:bg-white/10">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
                <button type="button" disabled={moviendo} onClick={() => ir(actuales + HORA)} className={boton}>+1 hora</button>
                <button type="button" disabled={moviendo} onClick={() => ir(actuales + DIA)} className={boton}>+1 día</button>
                <button type="button" disabled={moviendo} onClick={() => ir(actuales + 7 * DIA)} className={boton}>+1 semana</button>
            </div>
            <label className="block text-xs text-sky-100/70 mt-3" htmlFor="fecha-de-prueba-destino">Ir a un día y hora</label>
            <div className="flex gap-2 mt-1">
                <input id="fecha-de-prueba-destino" type="datetime-local" min={comoCampo(ahoraDeLaApp())} value={destino}
                    disabled={moviendo} onChange={(e) => setDestino(e.target.value)}
                    className="flex-1 min-w-0 rounded-lg bg-white/10 border border-white/15 px-3 py-2 text-sm text-white" />
                <button type="button" disabled={moviendo || objetivo <= actuales} onClick={irAlDestino}
                    className="rounded-lg bg-sky-500 text-black text-sm font-bold px-4 hover:bg-sky-400 disabled:opacity-50">
                    Ir
                </button>
            </div>
            <p className="text-[11px] text-sky-100/50 mt-3">Las horas, en la de este aparato. Solo hacia delante: para volver a empezar, otra cuenta.</p>
        </div>
    );
}
